import type { AxiosError } from 'axios';

import { isAclosetError } from './AclosetError';
import { AclosetMessageError } from './AclosetMessageError';

type ErrorData = {
  /**
   * https://github.com/looko-corp/acloset-api/blob/dev/errors/const.go
   */
  code?: string;
  message?: string;
  status?: number;
  requestID?: string;
};

/**
 * 서버 API 에러에서 원하는 메시지로 변환 후 전파하기 위한 용도입니다.
 */
export class AclosetServiceError extends AclosetMessageError {
  readonly status?: number;
  readonly requestID?: string;
  readonly serviceMessage?: string;

  /**
   * @deprecated
   */
  private aclosetServiceErrorData: ErrorData | undefined;

  /**
   * Axios 에러로부터 서비스 에러를 만듭니다.
   *
   * `messageOrCodeMap` 에는 커스터 메시지를 사용하건, 에러 코드 맵을 제공하면 됩니다.
   * `{ AUTH_UNAUTHORIZED: '사용자 인증에 실패했습니다.' }` 와 같이 제공하면 됩니다.
   *
   * @param messageOrCodeMap 사용자 메시지 또는 에러코드 기반 메시지 맵.
   */
  constructor(err: AxiosError<any, any>, messageOrCodeMap?: string | Record<string, string>) {
    const errorData = getAclosetServiceErrorData(err);
    const errorCode = errorData?.code;

    super(
      err,
      typeof messageOrCodeMap === 'string' ? messageOrCodeMap : errorCode ? messageOrCodeMap?.[errorCode] : undefined,
      errorCode,
    );

    this.status = errorData?.status;
    this.requestID = errorData?.requestID;
    this.serviceMessage = errorData?.message;
    this.aclosetServiceErrorData = getAclosetServiceErrorData(err);
  }

  /**
   * 서비스 에러 데이터.
   *
   * @deprecated data getter 사용.
   */
  errorData() {
    return this.aclosetServiceErrorData;
  }

  /**
   * 서비스 에러 데이터.
   *
   * @deprecated property 직접 사용.
   */
  get data() {
    return this.aclosetServiceErrorData;
  }
}

/**
 * AclosetServiceError 타입 체크.
 */
export const isAclosetServiceError = (obj: any): obj is AclosetServiceError => {
  return isAclosetError(obj) && 'requestID' in obj;
};

/**
 * Acloset 서버 에러 데이터를 가져옵니다.
 */
export const getAclosetServiceErrorData = (err: AxiosError<any, any>): ErrorData | undefined => {
  const data = err.response?.data;
  if (data != null && (data.code != null || data.message != null)) {
    return {
      code: data.code,
      message: data.message,
      status: 'status' in err && typeof err.status === 'number' ? err.status : undefined,
      requestID: err.config?.headers?.request_id,
    };
  }
  return undefined;
};
